import React, { useEffect, useRef, useState } from "react";

/**
 * Indicator dot primitive — four variants, state-driven animations.
 *
 * Variants:
 *   • `solid`   — static colored square (default). Color configurable
 *                 via the `color` prop; defaults to `accent` (ember-500).
 *   • `top`     — same as `solid`, top-padded to align with big numbers.
 *   • `status`  — slow 4s pulse, color by `state`
 *                 (disabled · success · pending · error).
 *                 `disabled` is steady (no pulse) — the rest blink.
 *                 `color` prop is ignored — status drives its own palette.
 *   • `connect` — wallet-flow indicator; animation by `state`
 *                 (disconnected · connecting · connected · error).
 *                 Consumers that need the success-flash overlay (e.g. the
 *                 wallet button on transition to connected) wrap this
 *                 component in their own button-level flash — the flash is
 *                 sized to the button, not the dot.
 *                 `color` prop is ignored — connect drives its own palette.
 *
 * Style-guide note: when building a new `solid` / `top` usage and a
 * color isn't specified, the dot renders in `accent` (ember-500). Only
 * override when the surrounding semantic context demands it (status
 * badges, graph series markers, tint accents, etc.).
 *
 * @version 4.5.1
 * @published 2026-05-14
 * @history
 *   4.5.1 — Re-phased `pulse` so the green flash fires at the START of
 *           each cycle (0–20%) instead of leading up to the END.
 *           Dropped the initial `animationDelay` — cycle 0% now lands
 *           at mount alongside the consumer's initial ramp-up, and
 *           every wrap at N×pulseIntervalMs coincides with the
 *           consumer's next `setInterval` tick. Pair with NumberFlow's
 *           `transformTiming` LONGER than the green hold (~40% of the
 *           cycle) so the flash starts together but snaps off
 *           mid-count-up — crisper "value changed" pulse without a
 *           drag-out.
 *   4.5.0 — Added `pulse` + `pulseIntervalMs` props to `solid` / `top`
 *           variants. When `pulse` is set, the dot flashes from its
 *           base color (set via `color`) to `success-base` and fades
 *           back, on a `pulseIntervalMs` cycle (default 1500ms). Use
 *           to signal live yield accrual on the active Pending row.
 *   4.4.0 — Added `countup` entry to the `pairWith` table for NumberFlow-
 *           rendered values at the number-lg scale. NumberFlow's wrapping
 *           span lowers the baseline relative to plain Text, so the dot
 *           needs more top padding than the standard `number-lg` pair.
 *   4.3.0 — `pairWith` now resolves to responsive `(size, alignPt)` —
 *           one tuple for mobile (<768px), one for ≥md. Tracks the
 *           responsive Text variant scale so the dot stays aligned at
 *           both breakpoints automatically. Implementation uses CSS
 *           custom properties + a colocated `<style>` block with a
 *           media query (inline `style` can't host media queries, and
 *           Tailwind JIT can't see runtime-generated arbitrary values).
 *   4.2.0 — Added `pairWith` prop on `solid` / `top` variants. Takes a
 *           Text variant name (hero / h1 / number-lg / number / body /
 *           small / fine-print) and auto-picks `size` + `alignPt` from
 *           the brand-ui pairing table. Use whenever a dot sits next to
 *           a Text element so alignment stays in sync if the type scale
 *           shifts.
 *   4.1.0 — Re-introduced `color` prop on `solid` / `top` variants as
 *           a typed union of semantic token aliases (brand + status +
 *           graph). Never accepts raw primitives. Defaults to `accent`.
 *   4.0.0 — Collapsed `pending` + `disconnected` variants into the new
 *           `connect` variant. Dropped `color` / `pulse` / `hoverColor`
 *           props in favor of a typed `state` enum per variant.
 *   3.0.0 — Added `pending` + `disconnected` variants (extracted from
 *           WalletConnectButton).
 *   2.0.0 — Added `variant` prop (top / solid / status).
 *   1.0.0 — Initial publish (ember square).
 */

type Variant = "solid" | "top" | "status" | "connect";
type StatusState = "disabled" | "success" | "pending" | "error";
type ConnectState = "disconnected" | "connecting" | "connected" | "error";

/**
 * Semantic color aliases accepted by the `color` prop on `solid` / `top`
 * variants. Keyed to existing semantic tokens — never primitives. Add
 * new entries here (and to `COLOR_TOKEN` below) when introducing new
 * semantic color families.
 */
type IndicationColor =
    // Brand
    | "accent"
    | "tint"
    // Status
    | "success"
    | "warning"
    | "error"
    | "info"
    | "disabled"
    // Graph palette (positional — matches --color-graph-* token names)
    | "graph-primary"
    | "graph-secondary"
    | "graph-tertiary"
    | "graph-quaternary"
    | "graph-quinary"
    | "graph-senary"
    | "graph-septenary"
    | "graph-octonary"
    | "graph-nonary"
    | "graph-denary";

/**
 * Pairing presets — when an IndicationUnit `top` dot sits next to a Text
 * variant, the dot's `size` and `alignPt` need to match the type's cap
 * height + ascender at each breakpoint. Hand-tuned per Solomon brand-ui
 * Text variants so callers don't have to think in pixels.
 *
 * Each entry has separate mobile (<768px) and desktop (≥md) values
 * because the Text variants themselves shift size at md (e.g. body is
 * text-base on mobile, md:text-lg on desktop). The dot follows.
 */
export type IndicationPairText =
    | "hero"
    | "h1"
    | "number-lg"
    | "number"
    | "body"
    | "small"
    | "fine-print"
    // `label` — Text variant `text-xs leading-4` non-responsive (no md
    // bump). Paired dot sized to fit a status-pill at this scale.
    | "label"
    // NumberFlow-rendered count-up values (e.g. AnimatedCurrency) at the
    // `number-lg` typography scale. NumberFlow wraps each digit in its own
    // span+padding to drive the slide animation, which shifts the visual
    // baseline a few pixels lower than a plain `<Text variant="number-lg">`.
    // Use `countup` whenever a `top` dot pairs with a NumberFlow value at
    // number-lg; reach for the regular `number-lg` entry for plain text.
    | "countup";

interface PairDimensions {
    size: { mobile: number; desktop: number };
    alignPt: { mobile: number; desktop: number };
}

const PAIR_DEFAULTS: Record<IndicationPairText, PairDimensions> = {
    hero: {
        size: { mobile: 12, desktop: 12 },
        alignPt: { mobile: 7, desktop: 11 }
    },
    h1: {
        size: { mobile: 7, desktop: 8 },
        alignPt: { mobile: 7, desktop: 7 }
    },
    "number-lg": {
        size: { mobile: 5, desktop: 8 },
        alignPt: { mobile: 8, desktop: 7 }
    },
    number: {
        size: { mobile: 5, desktop: 5 },
        alignPt: { mobile: 5, desktop: 7 }
    },
    body: {
        size: { mobile: 5, desktop: 5 },
        alignPt: { mobile: 7, desktop: 9 }
    },
    small: {
        size: { mobile: 5, desktop: 5 },
        alignPt: { mobile: 6, desktop: 6 }
    },
    "fine-print": {
        size: { mobile: 4, desktop: 4 },
        alignPt: { mobile: 4, desktop: 6 }
    },
    label: {
        // Pre-existing OnboardingShared.StatusBadge hand-coded a 6px dot
        // with text-xs typography; preserve that visual fidelity. Label
        // is non-responsive (text-xs leading-4 at all breakpoints) so
        // mobile/desktop sizes stay equal.
        size: { mobile: 6, desktop: 6 },
        alignPt: { mobile: 4, desktop: 4 }
    },
    // Empirical: NumberFlow's wrapping span shifts the visual baseline
    // a few px below plain Text at number-lg. alignPt + size hand-tuned
    // against AnimatedCurrency at both viewports.
    countup: {
        size: { mobile: 8, desktop: 8 },
        alignPt: { mobile: 16, desktop: 18 }
    }
};

// Responsive sizing: inline style can't host media queries, so we drive
// the dot's width/height + paddingTop from CSS custom properties and
// emit a colocated `<style>` block with the md breakpoint switch.
// Browsers dedupe identical CSS — fine for many instances.
//
// `iu-earn-pulse` keyframe powers the optional `pulse` prop. Green
// flashes at 0% of the cycle, holds through 20% (300ms of a 1500ms
// cycle), fades to base by 30%, holds base through 100%.
// Intentionally SHORTER than the consumer's count-up
// `transformTiming` so the green snaps off mid-count-up rather than
// co-resolving — a crisper "value just changed" flash.
//
// Delivered as `iu-pulse-earn`, an infinite CSS cycle. Sync with the
// consumer is approximate (CSS and setInterval drift by paint-cycle
// latency and setInterval jitter), which is fine for a decorative
// pulse. A one-shot `iu-pulse-earn-once` mode existed alongside it,
// re-keyed off a `pulseTrigger` prop for exact phase alignment; no
// caller in any app ever passed that prop, so both the mode and the
// prop are gone.
const INDICATION_RESPONSIVE_CSS = `
.iu-dot { width: var(--iu-size-m); height: var(--iu-size-m); }
.iu-top-pad { padding-top: var(--iu-align-m); }
@media (min-width: 768px) {
  .iu-dot { width: var(--iu-size-d); height: var(--iu-size-d); }
  .iu-top-pad { padding-top: var(--iu-align-d); }
}
@keyframes iu-earn-pulse {
  0%, 20%   { background-color: rgb(var(--color-success-base)); }
  30%, 100% { background-color: var(--iu-base-bg); }
}
.iu-pulse-earn {
  animation: iu-earn-pulse var(--iu-pulse-ms, 1500ms) ease-out infinite;
}
`;

const COLOR_TOKEN: Record<IndicationColor, string> = {
    accent: "rgb(var(--color-accent-base))",
    tint: "rgb(var(--color-tint-base))",
    success: "rgb(var(--color-success-base))",
    warning: "rgb(var(--color-warning-base))",
    error: "rgb(var(--color-error-base))",
    info: "rgb(var(--color-info-base))",
    disabled: "rgb(var(--color-disabled-base))",
    "graph-primary": "rgb(var(--color-graph-primary))",
    "graph-secondary": "rgb(var(--color-graph-secondary))",
    "graph-tertiary": "rgb(var(--color-graph-tertiary))",
    "graph-quaternary": "rgb(var(--color-graph-quaternary))",
    "graph-quinary": "rgb(var(--color-graph-quinary))",
    "graph-senary": "rgb(var(--color-graph-senary))",
    "graph-septenary": "rgb(var(--color-graph-septenary))",
    "graph-octonary": "rgb(var(--color-graph-octonary))",
    "graph-nonary": "rgb(var(--color-graph-nonary))",
    "graph-denary": "rgb(var(--color-graph-denary))"
};

interface IndicationUnitProps {
    variant?: Variant;
    /** Required for `status` and `connect` variants. Ignored for
     *  `solid` / `top`. */
    state?: StatusState | ConnectState;
    /** Color of the dot for `solid` / `top` variants. Accepts a
     *  semantic token alias — brand (`accent`, `tint`), status
     *  (`success`, `warning`, `error`, `info`, `disabled`), or graph
     *  palette (`graph-primary` through `graph-denary`). Defaults to
     *  `accent` (ember-500). Ignored for `status` and `connect`
     *  variants (those use their own state-driven palettes). */
    color?: IndicationColor;
    /** Size in pixels. Defaults to 8. Overridden by `pairWith` when set. */
    size?: number;
    /** Top padding in pixels when `variant="top"`. Defaults to 5.
     *  Overridden by `pairWith` when set. */
    alignPt?: number;
    /** When set, automatically picks `size` + `alignPt` matched to the
     *  given Text variant (per Solomon brand-ui Text scale). Use this
     *  whenever the dot pairs with a Text element so the alignment stays
     *  in sync if type tokens change. Wins over manual `size` / `alignPt`. */
    pairWith?: IndicationPairText;
    /** When true, the dot flashes from its base `color` to
     *  `success-base` and fades back on a repeating cycle. Use to signal
     *  live yield accrual paired with a NumberFlow count-up — set
     *  `pulseIntervalMs` to the count-up's tick period so the green
     *  flash lands on each tick. Only applies to `solid` / `top`
     *  variants; ignored on `status` / `connect`. Requires an inline
     *  `color` (not a Tailwind `className` background) so the keyframe
     *  has a base color to fade back to. */
    pulse?: boolean;
    /** Pulse cycle period in ms. Defaults to 1500ms (matches the
     *  retail Pending count-up's tick). Ignored when `pulse` is unset. */
    pulseIntervalMs?: number;
    /** Whether the parent is currently hovered. Only applies to
     *  `connect` + `state="disconnected"` — triggers the matrix rain. */
    hovered?: boolean;
    /** Tailwind utility classes for the dot (solid / top variants).
     *  When provided, wins over `color` — use this for semantic tokens
     *  outside the typed `color` enum (e.g. `bg-accent-reverse`). */
    className?: string;
}

/** Colors for the `status` variant — one per state. */
const STATUS_COLORS: Record<StatusState, string> = {
    disabled: "rgb(var(--color-disabled-base))",
    success: "rgb(var(--color-success-base))",
    pending: "rgb(var(--color-warning-base))",
    error: "rgb(var(--color-error-base))"
};

export function IndicationUnit({
    variant = "solid",
    state,
    color = "accent",
    size: sizeProp = 8,
    alignPt: alignPtProp = 5,
    pairWith,
    pulse = false,
    pulseIntervalMs = 1500,
    hovered = false,
    className
}: IndicationUnitProps): React.ReactElement {
    // pairWith picks responsive (mobile + desktop) size/alignPt from the
    // Text-variant lookup. If unset, both viewports collapse to the
    // caller-provided values (or component defaults) — no responsive shift.
    const pair = pairWith ? PAIR_DEFAULTS[pairWith] : null;
    const sizeMobile = pair?.size.mobile ?? sizeProp;
    const sizeDesktop = pair?.size.desktop ?? sizeProp;
    const alignMobile = pair?.alignPt.mobile ?? alignPtProp;
    const alignDesktop = pair?.alignPt.desktop ?? alignPtProp;

    if (variant === "status") {
        // Status/connect don't use pairWith (not supported per docs).
        // Render with a single size — desktop value collapses to sizeProp
        // when pairWith isn't set.
        return (
            <StatusDot
                state={(state as StatusState | undefined) ?? "success"}
                size={sizeDesktop}
            />
        );
    }

    if (variant === "connect") {
        return (
            <ConnectDot
                state={(state as ConnectState | undefined) ?? "disconnected"}
                size={sizeDesktop}
                hovered={hovered}
            />
        );
    }

    // solid / top — static colored square. Default color is `accent`
    // (ember-500); callers can pass any `IndicationColor` token alias
    // OR a `className` (Tailwind utility) that wins over the inline
    // backgroundColor. Sizing reads from CSS custom properties so the
    // md breakpoint can swap mobile → desktop values via the `<style>`
    // block above (`iu-dot` / `iu-top-pad` classes).
    //
    // When `pulse` is set, the keyframe needs a base color to fade
    // back to — we publish it as `--iu-base-bg` so the keyframe can
    // reference it. Pulse + a Tailwind `className` background isn't
    // supported (no inline base color → keyframe can't return cleanly);
    // documented on the prop.
    const dotStyle = {
        "--iu-size-m": `${sizeMobile}px`,
        "--iu-size-d": `${sizeDesktop}px`,
        ...(pulse
            ? {
                  "--iu-base-bg": COLOR_TOKEN[color],
                  "--iu-pulse-ms": `${pulseIntervalMs}ms`,
                  backgroundColor: `var(--iu-base-bg)`
                  // No animationDelay — cycle 0% lands at mount (paired
                  // with the consumer's initial ramp-up), and every
                  // wrap at N×pulseIntervalMs lands at the same moment
                  // the consumer's `setInterval` fires its next tick.
              }
            : className
              ? {}
              : { backgroundColor: COLOR_TOKEN[color] })
    } as unknown as React.CSSProperties;
    const pulseClassSuffix = pulse ? " iu-pulse-earn" : "";
    const dot = (
        <div
            className={`iu-dot shrink-0${pulseClassSuffix}${className ? ` ${className}` : ""}`}
            style={dotStyle}
        />
    );

    if (variant === "top") {
        return (
            <>
                <style
                    dangerouslySetInnerHTML={{
                        __html: INDICATION_RESPONSIVE_CSS
                    }}
                />
                <div
                    // `self-start` so the wrapper top-aligns to the parent
                    // regardless of `items-*` on the flex container. Without
                    // this, parents that use `items-center` (e.g.
                    // PanelTrigger's inner span) center the entire wrapper
                    // box and the `alignPt` top-padding gets swallowed —
                    // dot lands below the text's cap-height instead of at it.
                    // See `claude-docs/design-system/brand-ui.md` §
                    // IndicationUnit pairing for the original `items-start`
                    // caveat this prop nullifies.
                    className="iu-top-pad shrink-0 self-start"
                    style={
                        {
                            "--iu-align-m": `${alignMobile}px`,
                            "--iu-align-d": `${alignDesktop}px`
                        } as React.CSSProperties
                    }
                >
                    {dot}
                </div>
            </>
        );
    }

    return (
        <>
            <style
                dangerouslySetInnerHTML={{ __html: INDICATION_RESPONSIVE_CSS }}
            />
            {dot}
        </>
    );
}

/* ── Status ────────────────────────────────────────────── */

function StatusDot({
    state,
    size
}: {
    state: StatusState;
    size: number;
}): React.ReactElement {
    const color = STATUS_COLORS[state];
    const shouldPulse = state !== "disabled";
    return (
        <div
            className={`shrink-0 ${
                shouldPulse
                    ? "animate-[status-pulse_4s_ease-in-out_infinite]"
                    : ""
            }`}
            style={{
                width: size,
                height: size,
                backgroundColor: color
            }}
        />
    );
}

/* ── Connect ───────────────────────────────────────────── */

function ConnectDot({
    state,
    size,
    hovered
}: {
    state: ConnectState;
    size: number;
    hovered: boolean;
}): React.ReactElement {
    if (state === "connecting") {
        return <ConnectingCycle size={size} />;
    }

    if (state === "disconnected") {
        return <DisconnectedMatrix size={size} hovered={hovered} />;
    }

    if (state === "connected") {
        // Same visual as status state="success" — steady pulsing green.
        return <StatusDot state="success" size={size} />;
    }

    // error — red, burst-then-slow
    return (
        <div
            className="shrink-0"
            style={{
                width: size,
                height: size,
                backgroundColor: STATUS_COLORS.error,
                animation:
                    "wallet-dot-error-burst 800ms ease-out forwards, wallet-dot-error-slow 2.5s ease-in-out 800ms infinite"
            }}
        />
    );
}

/* ── Connecting — four-dot cycle ───────────────────────── */

/**
 * Four dots pulse outward-and-back (phase 1 of the connecting cycle).
 */
function DotPulse({ color }: { color: string }): React.ReactElement {
    const cls = "absolute size-1";
    const dot = (dx: string, dy: string): React.CSSProperties =>
        ({
            top: "50%",
            left: "50%",
            marginTop: -2,
            marginLeft: -2,
            backgroundColor: color,
            "--dx": dx,
            "--dy": dy,
            animation: "dot-pulse 2s ease-in-out infinite"
        }) as React.CSSProperties;
    return (
        <div className="relative shrink-0 size-2">
            <div className={cls} style={dot("-5px", "-5px")} />
            <div className={cls} style={dot("5px", "-5px")} />
            <div className={cls} style={dot("-5px", "5px")} />
            <div className={cls} style={dot("5px", "5px")} />
        </div>
    );
}

/**
 * Four dots rotate clockwise one full cycle (phase 2).
 */
function DotRotate({ color }: { color: string }): React.ReactElement {
    const cls = "absolute size-1";
    const base: React.CSSProperties = {
        top: "50%",
        left: "50%",
        marginTop: -2,
        marginLeft: -2,
        backgroundColor: color
    };
    return (
        <div className="relative shrink-0 size-2">
            <div
                className={cls}
                style={{
                    ...base,
                    animation: "dot-rotate-tl 3s ease-in-out forwards"
                }}
            />
            <div
                className={cls}
                style={{
                    ...base,
                    animation: "dot-rotate-tr 3s ease-in-out forwards"
                }}
            />
            <div
                className={cls}
                style={{
                    ...base,
                    animation: "dot-rotate-bl 3s ease-in-out forwards"
                }}
            />
            <div
                className={cls}
                style={{
                    ...base,
                    animation: "dot-rotate-br 3s ease-in-out forwards"
                }}
            />
        </div>
    );
}

/**
 * Loops pulse (6s) → rotate (3s). Re-keyed each phase flip so the
 * rotate animation replays on every cycle.
 */
function ConnectingCycle({ size }: { size: number }): React.ReactElement {
    const [phase, setPhase] = useState<"pulse" | "rotate">("pulse");
    const [key, setKey] = useState(0);

    useEffect(() => {
        const duration = phase === "pulse" ? 6000 : 3000;
        const timer = setTimeout(() => {
            setPhase((p) => (p === "pulse" ? "rotate" : "pulse"));
            setKey((k) => k + 1);
        }, duration);
        return (): void => clearTimeout(timer);
    }, [phase, key]);

    const color = "rgb(var(--color-warning-base))";
    const scale = size / 8;
    return (
        <div className="shrink-0" style={{ width: size, height: size }}>
            <div
                style={{
                    width: 8,
                    height: 8,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left"
                }}
            >
                <React.Fragment key={key}>
                    {phase === "rotate" ? (
                        <DotRotate color={color} />
                    ) : (
                        <DotPulse color={color} />
                    )}
                </React.Fragment>
            </div>
        </div>
    );
}

/* ── Disconnected — matrix rain on hover ──────────────── */

interface RainPixel {
    wave: number;
    x: number;
    y: number;
    delay: number;
    duration: number;
}

function generateRainPixels(): RainPixel[] {
    const pixels: RainPixel[] = [];

    // Wave 1: columns 1, 3, 6 (0-indexed: 0, 2, 5)
    const wave1Cols = [0, 2, 5];
    const wave1Offsets = [0, 50, 20];
    wave1Cols.forEach((col, ci) => {
        for (let row = 0; row < 8; row++) {
            const delay = wave1Offsets[ci] + row * 40;
            const duration = 200 + (((col * 3 + row * 7) % 5) - 2) * 25;
            pixels.push({ wave: 1, x: col, y: row, delay, duration });
        }
    });

    // Wave 2: columns 4, 8 (0-indexed: 3, 7). Trigger ~5/8 through wave 1.
    const wave2Start = 206;
    const wave2Cols = [3, 7];
    const wave2Offsets = [0, 40];
    wave2Cols.forEach((col, ci) => {
        for (let row = 0; row < 8; row++) {
            const delay = wave2Start + wave2Offsets[ci] + row * 40;
            const duration = 200 + (((col * 5 + row * 3) % 5) - 2) * 25;
            pixels.push({ wave: 2, x: col, y: row, delay, duration });
        }
    });

    // Wave 3: columns 2, 5, 7 (0-indexed: 1, 4, 6). Trigger ~7/8 through wave 2.
    const wave3Start = 460;
    const wave3Cols = [1, 4, 6];
    const wave3Offsets = [0, 30, 60];
    wave3Cols.forEach((col, ci) => {
        for (let row = 0; row < 8; row++) {
            const delay = wave3Start + wave3Offsets[ci] + row * 40;
            const duration = 200 + (((col * 11 + row * 2) % 5) - 2) * 25;
            pixels.push({ wave: 3, x: col, y: row, delay, duration });
        }
    });

    return pixels;
}

const RAIN_PIXELS: RainPixel[] = generateRainPixels();

function DisconnectedMatrix({
    size,
    hovered
}: {
    size: number;
    hovered: boolean;
}): React.ReactElement {
    const [key, setKey] = useState(0);
    const prevHovered = useRef(false);

    useEffect(() => {
        if (hovered && !prevHovered.current) {
            setKey((k) => k + 1);
        }
        prevHovered.current = hovered;
    }, [hovered]);

    const scale = size / 8;

    return (
        <div
            className="shrink-0"
            style={{ width: size, height: size, overflow: "visible" }}
        >
            <div
                className="relative"
                style={{
                    width: 8,
                    height: 8,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    overflow: "visible"
                }}
            >
                <div
                    className="absolute inset-0 transition-colors duration-300"
                    style={{
                        backgroundColor: hovered
                            ? "rgb(var(--color-foreground-base))"
                            : "rgb(var(--color-disabled-base))"
                    }}
                />
                {hovered && (
                    <div key={key}>
                        {RAIN_PIXELS.map(({ wave, x, y, delay, duration }) => (
                            <div
                                key={`${wave}-${x}-${y}`}
                                className="absolute"
                                style={{
                                    left: x,
                                    top: y,
                                    width: 1,
                                    height: 1,
                                    backgroundColor:
                                        "rgb(var(--color-success-base))",
                                    animation: `dot-matrix ${duration}ms ease-out ${delay}ms forwards`,
                                    opacity: 0
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
